import { useCallback, useEffect, useRef, useState } from "react";
import { HomeScene } from "./HomeScene";
import { CONTACT } from "./bays";
import { LensWipe } from "./LensWipe";
import { RaceMode } from "./race/RaceMode";
import { armAudio } from "./race/raceAudio";
import { fmtLap } from "./race/raceTrack";
import { DroneCursor } from "./DroneCursor";
import { EGGS, logbook } from "./logbook";
import type { LaunchRequest } from "./MenuDrones";

const KONAMI = [
  "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a",
];

/**
 * The free.vet main page. The 3D hero owns the viewport: the launch grid
 * becomes the FREE.VET wordmark, then the flight-line menu illuminates.
 * DOM carries the tagline, contact links, the lens-wipe transition, and the
 * egg layer: the checkered flag (race mode), the Konami roll, the encore,
 * the pilot logbook, and the earned drone cursor.
 */
export default function HomePage() {
  const [settled, setSettled] = useState(false);
  const [launch, setLaunch] = useState<LaunchRequest | null>(null);
  const [raceOpen, setRaceOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [log, setLog] = useState(logbook.state());
  const onSettled = useCallback(() => setSettled(true), []);
  const onLaunch = useCallback((req: LaunchRequest) => setLaunch(req), []);

  useEffect(() => logbook.subscribe(setLog), []);

  // Coming BACK from a bay (browser Back) restores this page from the
  // bfcache with its React tree intact, so the lens wipe that covered the
  // screen on the way out is still covering it. Clear it on every pageshow:
  // a genuinely fresh load has nothing to clear anyway.
  useEffect(() => {
    const onShow = () => setLaunch(null);
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  // egg: the old code barrel-rolls the whole page
  useEffect(() => {
    let i = 0;
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      i = k === KONAMI[i] ? i + 1 : k === KONAMI[0] ? 1 : 0;
      if (i === KONAMI.length) {
        i = 0;
        logbook.found("konami");
        const el = document.querySelector(".page");
        if (el) {
          el.classList.add("roll");
          window.setTimeout(() => el.classList.remove("roll"), 1350);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // egg: triple-click the tagline = an encore (the show launches again)
  const tagClicks = useRef<number[]>([]);
  const onTagClick = () => {
    const now = performance.now();
    tagClicks.current = [...tagClicks.current.filter((t) => now - t < 700), now];
    if (tagClicks.current.length >= 3) {
      logbook.found("encore");
      window.setTimeout(() => window.location.reload(), 250);
    }
  };

  const startRace = () => {
    armAudio(); // inside the click gesture, so the hum is allowed to sing
    logbook.found("race-flag");
    setBookOpen(false);
    setRaceOpen(true);
  };

  return (
    <div className="page">
      <div className="scene">
        <HomeScene
          settled={settled}
          onSettled={onSettled}
          launchingId={launch ? launch.bay.id : null}
          onLaunch={onLaunch}
        />
      </div>

      <div className={"overlay" + (settled ? " show" : "")}>
        <p className="tagline" onClick={onTagClick}>
          Welcome to my corner of the internet. I'm a person who engineers
          systems that race, fly, glow, print, think... Here you can play with
          all of them.
        </p>
        <footer className="contact">
          {CONTACT.map((c) => (
            <a key={c.label} href={c.href} target="_blank" rel="noopener noreferrer">
              {c.label}
            </a>
          ))}
        </footer>
      </div>

      {settled && !raceOpen && (
        <button className="flag-egg" onClick={startRace} aria-label="A checkered flag">
          <svg viewBox="0 0 30 34" width="30" height="34">
            <line x1="4" y1="2" x2="4" y2="32" stroke="#aab2c4" strokeWidth="2.4" strokeLinecap="round" />
            <g>
              <rect x="5" y="3" width="6" height="5" fill="#e8ecf4" />
              <rect x="11" y="3" width="6" height="5" fill="#14161d" />
              <rect x="17" y="3" width="6" height="5" fill="#e8ecf4" />
              <rect x="5" y="8" width="6" height="5" fill="#14161d" />
              <rect x="11" y="8" width="6" height="5" fill="#e8ecf4" />
              <rect x="17" y="8" width="6" height="5" fill="#14161d" />
              <rect x="5" y="13" width="6" height="5" fill="#e8ecf4" />
              <rect x="11" y="13" width="6" height="5" fill="#14161d" />
              <rect x="17" y="13" width="6" height="5" fill="#e8ecf4" />
            </g>
          </svg>
        </button>
      )}

      {log.eggs.length > 0 && !raceOpen && (
        <button className="logbook-chip" onClick={() => setBookOpen((o) => !o)}>
          Logbook {log.eggs.length}/{EGGS.length}
        </button>
      )}
      {bookOpen && !raceOpen && (
        <div className="logbook-pop">
          <div className="lb-title">Pilot logbook</div>
          {EGGS.map((e) => (
            <div key={e.id} className={"lb-row" + (log.eggs.includes(e.id) ? " got" : "")}>
              {log.eggs.includes(e.id) ? e.label : "? ? ?"}
            </div>
          ))}
          <div className="lb-stats">
            Laps {log.laps}
            {log.best !== null ? " · best " + fmtLap(log.best) : ""}
          </div>
          {log.laps > 5 ? (
            <div className="lb-unlock">Drone cursor unlocked, pilot</div>
          ) : (
            <div className="lb-unlock dim">Fly more than 5 laps to earn the drone cursor</div>
          )}
        </div>
      )}

      {raceOpen && <RaceMode onExit={() => setRaceOpen(false)} />}

      {launch && (
        <LensWipe
          bay={launch.bay}
          x={launch.x}
          y={launch.y}
          onClose={() => setLaunch(null)}
        />
      )}

      <DroneCursor active={log.laps > 5 && !raceOpen} />
    </div>
  );
}
